import React from 'react';

import workspace from "../../../canvas/core/workspace";
import {enableSlabMode} from "../../../canvas/store/canvasHelper";
import store from "../../../../../../store";
import {showSlabTool} from "../../../../../../actions/sqftSlabFunctions";
import {openModalFix} from "../../../../../../actions/sqftModalFunctions";
import SlabConflictWarning from "./SlabConflictWarning";
import {getShapesCenter} from "../../../../helpers/conflictPopupHelper";
import workspaceZoom from "../../../canvas/core/workspaceZoom";
import sqftSlabManager from "../../../canvas/store/sqftSlabManager";
import sqftShapeManager from "../../../canvas/store/sqftShapeManager";
import {getCurrentLayout} from "../../../../../appLayout";
import {MODAL_FIX_CLOSE} from "../../../../../../actions/actionTypes";

/**
 *  Use slabConflictIdsParam if it is a non-empty array, otherwise fallback to store
 * @param shapeIdsWithConflict
 * @return {Promise<void>}
 */
export const handleOpenSlabModeConflict = async (shapeIdsWithConflict) => {
  const state = store.getState();

  const slabConflictIdsFromStore = state?.sqftSlabState?.sqftSlabConflicts ?? [];
  const slabConflictIds =
    Array.isArray(shapeIdsWithConflict) && shapeIdsWithConflict.length > 0
      ? shapeIdsWithConflict
      : slabConflictIdsFromStore;

  const { isModalFixOpen } = state.sqftModalFixState;
  const { wizardConflictingSteps } = state.wizardState;
  const intl = workspace.intl;

  if (wizardConflictingSteps?.length > 0) {
    return;
  }

  if (!slabConflictIds.length) {
    store.dispatch({ type: MODAL_FIX_CLOSE });
    return;
  }

  if (isModalFixOpen) return;

  const steps = [];

  // In this version we show single step for all conflicting shapes
  steps.push({
    id: 'slabModeConflict',
    title: intl.formatMessage({ id: "sqft.popup.slabMode.conflict.title" }),
    // Content should now work with shapes instead of ids
    content: getSlabConflictContent(slabConflictIds),
    params: {
      slabConflictIds,
      // materialId: you can derive it from shapes if needed, example:
      // materialId: shapesWithConflict[0]?.materialId ?? null,
    },
    showClose: false,
    showCancel: true,
    showSkip: false,
    showPrev: false,
    isNextButtonDisabled: true,
    nextButtonText: 'Finish',
    enterAnim: 'fadeDown',
    exitAnim: 'fadeUp',
    onStepEnter: (stepData) => {
      const { slabConflictIds } = stepData.params;
      if (slabConflictIds) {
        const shapesWithConflict = workspace.getShapesByIds(slabConflictIds);

        // Highlight conflicting shapes (now function should accept shapes, not ids)
        highlightConflictingShapes(shapesWithConflict);

        // Zoom to conflict area
        setTimeout(() => {
          const shapes = shapesWithConflict || [];

          if (shapes.length > 0) {
            const centerPoint = getShapesCenter(shapes);
            workspaceZoom.zoomTo(centerPoint, 0.1);
          }
        }, 500);
      }
    },
    onStepExit: (stepData) => {
      // Clear highlighting if needed
      void stepData;
    }
  });

  // Switch to slab mode
  enableSlabMode();
  store.dispatch(showSlabTool(true));

  store.dispatch(openModalFix('slabModeConflict', steps, () => {
    console.log('slab mode conflicts resolved');
  }));
};

// Helper to get sharedFeatureName for a shape
const getShapeSharedFeatureName = (shape) => {
  // If shape has direct feature getter - use it first
  if (typeof shape.getFeature === 'function') {
    const feature = shape.getFeature();
    return feature?.dbFeature?.sharedFeatureName ?? null;
  }

  const layout = getCurrentLayout();
  // Fallback: via layout + featureId (old way)
  if (shape.featureId != null) {
    const feature = layout.getFeatureById(shape.featureId, true);
    return feature?.dbFeature?.sharedFeatureName ?? null;
  }

  return null;
};

// Find shapes with and without conflicts in slab mode
export const findSlabConflictShapes = () => {
  const allInvolvedShapes = new Set();   // all shapes that overlap with any placeholder
  const conflictedShapes = new Set();    // shapes that have different sharedFeatureName than placeholder

  const slabPlaceHolderShapes = sqftSlabManager.getSlabPlaceHolderShapes();
  const slabImageShapes = sqftSlabManager.getSlabImageShapes();

  for (const slabPlaceHolderShape of [...slabPlaceHolderShapes, ...slabImageShapes]) {
    const overlappedIds =
      slabPlaceHolderShape.shapeIntersections?.overlappedWithShapeIds || [];

    if (overlappedIds.length === 0) {
      continue;
    }

    const shapes = sqftShapeManager.getShapeByIds(overlappedIds) || [];
    if (shapes.length === 0) {
      continue;
    }

    // Get placeholder feature shared name
    const placeholderFeature =
      typeof slabPlaceHolderShape.getFeature === 'function'
        ? slabPlaceHolderShape.getFeature()
        : null;

    const placeholderSharedName = placeholderFeature?.dbFeature?.sharedFeatureName ?? null;

    // If we cannot determine placeholder shared name, skip this placeholder
    if (!placeholderSharedName) {
      continue;
    }

    for (const shape of shapes) {
      allInvolvedShapes.add(shape);

      const shapeSharedName = getShapeSharedFeatureName(shape);

      // If shape has different sharedFeatureName than placeholder -> conflict
      if (shapeSharedName && shapeSharedName !== placeholderSharedName && !shape.isHidden) {
        conflictedShapes.add(shape);
      }
    }
  }

  const shapesWithConflict = Array.from(conflictedShapes);
  const shapesWithoutConflict = Array.from(allInvolvedShapes)
    .filter(shape => !conflictedShapes.has(shape));

  return {
    shapesWithConflict,
    shapesWithoutConflict,
  };
};

// Apply isWithConflict flags to shapes
const applySlabConflictFlags = ({ shapesWithConflict, shapesWithoutConflict }) => {
  // First reset for shapes without conflicts
  for (const shape of shapesWithoutConflict) {
    shape.isWithConflict = false;
  }

  // Then set conflict flag for conflicted shapes
  for (const shape of shapesWithConflict) {
    shape.isWithConflict = true;
  }
};

const resetAllSlabConflicts = () => {
  const shapes = workspace.getShapesWithoutAttachedResources();
  for (const shape of shapes) {
    shape.isWithConflict = false;
  }
}

// Public function: detect conflicts in slab mode
export const detectSlabConflicts = () => {
  const { shapesWithConflict, shapesWithoutConflict } = findSlabConflictShapes();

  resetAllSlabConflicts();
  applySlabConflictFlags({
    shapesWithConflict,
    shapesWithoutConflict,
  });

  // return shape Ids
  return (shapesWithConflict || [])
    .map(shape => shape?.id)
    .filter(Boolean);
};

// Get content component for slab conflict
const getSlabConflictContent = (conflict) => {
  return React.createElement(SlabConflictWarning, {
    conflict: conflict
  });
};

// Highlight shapes with conflict
const highlightConflictingShapes = (sqftIds) => {
  const shapes = workspace.getShapesByIds(sqftIds);
  for (const shape of shapes) {
    //shape.isWithConflict = true;
    shape.shapeRender.blink.start({ blinkMs: 1400, totalMs: 5000, colorOverride: 'rgba(255,0,0,0.6)' });
  }
};
